import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ScoreInfo } from "@/components/mai/Explain";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { MB_PALETTE } from "@/components/mai/constants";
import { Warning, ArrowRight, PencilSimple, Check, X, CurrencyDollar } from "@phosphor-icons/react";
import { CommentBadge, CommentThread } from "@/components/mai/Comments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEV_STYLE = { Critical: "bg-red-600 text-white", High: "bg-amber-500 text-white", Moderate: "bg-slate-900 text-white", Low: "bg-slate-200 text-slate-600" };
const EFFORT_STYLE = {
  Low: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Medium: "bg-amber-50 text-amber-700 border border-amber-300",
  High: "bg-red-50 text-red-700 border border-red-200",
};
const EFFORT_ORDER = { Low: 0, Medium: 1, High: 2 };

function ExecutionStrip({ gap, productId, options, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const ex = gap.execution;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/gap-engine/${productId}/override`, { gap_key: gap.gap_key, ...form });
      toast.success("Execution estimate updated");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!ex) return null;
  if (editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`gap-exec-edit-${gap.gap_key}`}>
        {[["effort_tier", options.effort_tiers], ["estimated_timeline", options.timelines], ["estimated_cost_band", options.cost_bands]].map(([k, opts]) => (
          <select key={k} data-testid={`gap-exec-${k}-${gap.gap_key}`} value={form[k] ?? ex[k]}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className="text-[11px] border border-slate-300 px-2 py-1.5 bg-white">
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        <button data-testid={`gap-exec-save-${gap.gap_key}`} disabled={saving} onClick={save} className="text-emerald-700 p-1 hover:bg-emerald-50"><Check size={14} weight="bold" /></button>
        <button onClick={() => { setEditing(false); setForm({}); }} className="text-slate-400 p-1 hover:bg-slate-100"><X size={14} /></button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`gap-exec-${gap.gap_key}`}>
      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${EFFORT_STYLE[ex.effort_tier]}`}>{ex.effort_tier} effort</span>
      <span className="text-[10px] font-mono-data px-2 py-0.5 border border-slate-200 text-slate-600">{ex.estimated_timeline}</span>
      <span className="text-[10px] font-mono-data px-2 py-0.5 border border-slate-200 text-slate-600">{ex.estimated_cost_band}</span>
      {ex.overridden.length > 0 && <span className="text-[9px] uppercase tracking-wider text-blue-600">edited</span>}
      <button data-testid={`gap-exec-edit-btn-${gap.gap_key}`} onClick={() => setEditing(true)} className="text-slate-300 hover:text-slate-600 p-0.5"><PencilSimple size={12} /></button>
    </div>
  );
}

function ImpactCallout({ gap, productId, onSaved }) {
  const io = gap.impact_of_inaction;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(io?.at_risk_revenue || "");
  if (!io) return null;

  const save = async () => {
    try {
      await axios.put(`${API}/gap-engine/${productId}/override`, {
        gap_key: gap.gap_key,
        effort_tier: gap.execution.overridden.includes("effort_tier") ? gap.execution.effort_tier : "",
        estimated_timeline: gap.execution.overridden.includes("estimated_timeline") ? gap.execution.estimated_timeline : "",
        estimated_cost_band: gap.execution.overridden.includes("estimated_cost_band") ? gap.execution.estimated_cost_band : "",
        at_risk_revenue: value,
      });
      toast.success("At-risk revenue saved");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div data-testid={`gap-impact-${gap.gap_key}`} className="mt-2.5 border-l-4 border-red-600 bg-red-50/70 px-3.5 py-2.5">
      <p className="text-[10px] uppercase tracking-widest font-bold text-red-700">Estimated Impact of Inaction · +{io.delay_months} months</p>
      <p className="text-[11px] text-red-900/80 leading-relaxed mt-1">{io.note}</p>
      <div className="flex items-center gap-2 mt-1.5">
        <CurrencyDollar size={12} className="text-red-700 shrink-0" />
        {editing ? (
          <>
            <input data-testid={`gap-revenue-input-${gap.gap_key}`} value={value} onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. ~€2.5M first-year revenue at risk" className="text-[11px] border border-red-200 bg-white px-2 py-1 flex-1 max-w-sm" />
            <button data-testid={`gap-revenue-save-${gap.gap_key}`} onClick={save} className="text-emerald-700 p-0.5"><Check size={13} weight="bold" /></button>
            <button onClick={() => setEditing(false)} className="text-slate-400 p-0.5"><X size={13} /></button>
          </>
        ) : (
          <>
            <p className="text-[11px] text-red-900/90 font-semibold">{io.at_risk_revenue || "At-risk revenue: not estimated yet"}</p>
            <button data-testid={`gap-revenue-edit-${gap.gap_key}`} onClick={() => setEditing(true)} className="text-red-400 hover:text-red-700 p-0.5"><PencilSimple size={11} /></button>
          </>
        )}
      </div>
    </div>
  );
}

const GapRow = ({ gap, showCountry, productId, options, onSaved, commentSummary, onCommentsChanged }) => {
  const [threadOpen, setThreadOpen] = useState(false);
  return (
  <div className="px-5 py-4 flex gap-4 items-start" data-testid={`gap-row-${gap.gap_key}`}>
    <span className={`shrink-0 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${SEV_STYLE[gap.gap_severity.label]}`}>{gap.gap_severity.label}</span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-900">
        {showCountry && <span className="mr-1.5">{gap.flag} {gap.country_name} ·</span>}
        {gap.requirement_type}
        <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 border border-slate-200 px-1.5 py-0.5">{gap.level} · {gap.gap_status === "missing" ? "Missing" : "Adaptation needed"}</span>
        <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500 border border-slate-300 px-1.5 py-0.5">Strategic: {gap.strategic_importance.label}</span>
        <span className="ml-2 inline-block align-middle"><CommentBadge summary={commentSummary} entityKey={gap.gap_key} onClick={() => setThreadOpen(!threadOpen)} /></span>
      </p>
      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{gap.detail}</p>
      <ExecutionStrip gap={gap} productId={productId} options={options} onSaved={onSaved} />

      {gap.available_now && (
        <div data-testid={`gap-available-${gap.gap_key}`} className="mt-2.5 border-l-4 border-emerald-600 bg-emerald-50/60 px-3.5 py-2">
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Available Now</p>
          <p className="text-[11px] text-slate-700 mt-0.5">
            <Link to="/console/inventory" className="font-semibold text-emerald-800 hover:underline">{gap.available_now.asset_name}</Link>
            <span className="text-slate-500"> — {gap.available_now.note}</span>
          </p>
        </div>
      )}
      {gap.must_generate && (
        <div data-testid={`gap-generate-${gap.gap_key}`} className="mt-2 border-l-4 border-slate-400 bg-slate-50 px-3.5 py-2">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Must Be Newly Generated</p>
          <p className="text-[11px] text-slate-600 leading-relaxed mt-0.5">{gap.must_generate}</p>
          {gap.no_related_evidence ? (
            <p className="text-[10px] text-slate-400 mt-1 italic">No related evidence currently exists in the inventory.</p>
          ) : gap.best_asset && (
            <p className="text-[10px] text-slate-400 mt-1">Closest related asset: <Link to="/console/inventory" className="text-slate-600 hover:underline">{gap.best_asset}</Link> (insufficient for this requirement).</p>
          )}
        </div>
      )}
      <ImpactCallout gap={gap} productId={productId} onSaved={onSaved} />
      {threadOpen && <CommentThread productId={productId} entityType="gap" entityKey={gap.gap_key} onChanged={onCommentsChanged} />}
    </div>
    <p className="font-heading font-extrabold text-lg text-slate-700">{gap.priority_index}</p>
  </div>
  );
};

const SORTS = {
  priority: { label: "Priority", fn: (a, b) => b.priority_index - a.priority_index },
  effort: { label: "Effort", fn: (a, b) => EFFORT_ORDER[b.execution.effort_tier] - EFFORT_ORDER[a.execution.effort_tier] },
  timeline: { label: "Timeline", fn: null },
  cost: { label: "Cost", fn: null },
};

export default function GapsPage() {
  const { productId } = useProduct();
  const [data, setData] = useState(null);
  const [sortBy, setSortBy] = useState("priority");
  const [comments, setComments] = useState({});

  const load = useCallback(() => {
    axios.get(`${API}/gap-engine/${productId}`).then((r) => setData(r.data)).catch(console.error);
  }, [productId]);

  const loadComments = useCallback(() => {
    axios.get(`${API}/comments/${productId}/summary`, { params: { entity_type: "gap" } })
      .then((r) => setComments(r.data)).catch(console.error);
  }, [productId]);

  useEffect(() => { setData(null); setComments({}); load(); loadComments(); }, [load, loadComments]);

  const opts = data?.execution_options || { effort_tiers: [], timelines: [], cost_bands: [] };
  const sortFn = {
    priority: (a, b) => b.priority_index - a.priority_index,
    effort: (a, b) => EFFORT_ORDER[b.execution?.effort_tier] - EFFORT_ORDER[a.execution?.effort_tier],
    timeline: (a, b) => opts.timelines.indexOf(b.execution?.estimated_timeline) - opts.timelines.indexOf(a.execution?.estimated_timeline),
    cost: (a, b) => opts.cost_bands.indexOf(b.execution?.estimated_cost_band) - opts.cost_bands.indexOf(a.execution?.estimated_cost_band),
  }[sortBy];
  const highPriority = data ? [...data.high_priority_evidence].sort(sortFn) : [];

  return (
    <PageShell pageId="gaps" overline="Evidence Gap Engine · Layer 7" title="Evidence Gap Analysis">
      {!data ? (
        <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Analysing gaps…</div>
      ) : (
        <>
          <div data-testid="gap-summary" className="grid grid-cols-2 md:grid-cols-5 border border-slate-200 bg-white divide-x divide-y md:divide-y-0 divide-slate-200">
            {[
              { k: "total_requirements", label: "Requirements" },
              { k: "covered_as_is", label: "Covered As-Is", color: "#047857" },
              { k: "reusable_with_adaptation", label: "Reusable w/ Adaptation", color: "#B45309" },
              { k: "missing", label: "Missing", color: "#B91C1C" },
              { k: "coverage_rate", label: "Coverage Rate %" },
            ].map((c) => (
              <div key={c.k} data-testid={`gap-kpi-${c.k}`} className="p-6">
                <p className="overline-label text-slate-400">{c.label}</p>
                <p className="font-heading font-extrabold text-4xl tracking-tight mt-3" style={c.color ? { color: c.color } : undefined}>{data.summary[c.k]}</p>
              </div>
            ))}
          </div>

          <section data-testid="top-gaps" className="bg-white border border-slate-200">
            <div className="p-6 border-b border-slate-200 flex flex-wrap items-center gap-3">
              <Warning size={20} weight="fill" className="text-amber-600" />
              <div className="flex-1">
                <p className="overline-label text-slate-400">Severity × Strategic Importance · Execution Plan</p>
                <h2 className="font-heading font-bold text-lg tracking-tight">High Priority Evidence <ScoreInfo scoreId="gap_priority" /></h2>
              </div>
              <div className="flex items-center gap-1">
                <span className="overline-label text-slate-400 mr-1">Sort by</span>
                {Object.entries(SORTS).map(([k, v]) => (
                  <button key={k} data-testid={`gap-sort-${k}`} onClick={() => setSortBy(k)}
                    className={`text-[11px] font-semibold px-2.5 py-1.5 border transition-colors ${sortBy === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {highPriority.map((g, i) => <GapRow key={`${g.country_id}-${g.requirement_type}-${i}`} gap={g} showCountry productId={productId} options={opts} onSaved={load} commentSummary={comments[g.gap_key]} onCommentsChanged={loadComments} />)}
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading font-bold text-lg tracking-tight">Bucket-Level Evidence Workstreams</h2>
            <div data-testid="bucket-gaps" className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {data.by_bucket.filter((b) => b.gap_count > 0).map((b, i) => (
                <div key={b.bucket_id} className="bg-white border border-slate-200" style={{ borderTop: `3px solid ${MB_PALETTE[i % MB_PALETTE.length]}` }}>
                  <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <p className="font-heading font-bold tracking-tight text-sm">{b.bucket_id} · {b.members.join(", ")}</p>
                    <span className="font-mono-data text-xs text-slate-500">{b.gap_count} gaps</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {b.shared_gaps.map((s) => (
                      <div key={s.requirement_type} className="px-5 py-3">
                        <p className="text-sm font-semibold text-slate-900">{s.requirement_type}
                          <span className={`ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${SEV_STYLE[s.avg_severity.label]}`}>{s.avg_severity.label}</span>
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{s.bucket_action}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading font-bold text-lg tracking-tight">Coverage by Market</h2>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {data.by_country.map((c) => (
                <div key={c.id} data-testid={`country-gaps-${c.id}`} className="bg-white border border-slate-200">
                  <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
                    <span className="text-xl">{c.flag}</span>
                    <p className="font-heading font-bold tracking-tight flex-1">{c.name}</p>
                    <div className="w-28">
                      <div className="flex justify-between text-[10px] text-slate-400 mb-1"><span>Coverage</span><span>{c.coverage_rate}%</span></div>
                      <div className="h-1.5 bg-slate-100"><div className="h-full bg-emerald-600" style={{ width: `${c.coverage_rate}%` }} /></div>
                    </div>
                    <span className="font-mono-data text-xs text-slate-500">{c.gap_count ?? c.gaps.length} gaps</span>
                  </div>
                  {c.gaps.length === 0 ? (
                    <p className="px-5 py-6 text-sm text-emerald-700">No evidence gaps — portfolio fully covers requirements.</p>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {c.gaps.map((g, i) => <GapRow key={`${g.requirement_type}-${i}`} gap={g} productId={productId} options={opts} onSaved={load} commentSummary={comments[g.gap_key]} onCommentsChanged={loadComments} />)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
